/**
 * Date Utilities for Meeting Management
 * Handles date/time formatting, parsing, and validation
 */

import type { Meeting, MeetingStatus } from "@/types/meeting.types";

/**
 * Format meeting date for display
 * @param dateString - ISO 8601 date (YYYY-MM-DD)
 * @returns Formatted date string (e.g., "Jan 15, 2025")
 */
export function formatMeetingDate(dateString: string): string {
  const date = new Date(dateString + "T00:00:00");
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Format meeting time for display
 * @param timeString - 24h format time (HH:MM)
 * @returns Formatted time string (e.g., "2:00 PM")
 */
export function formatMeetingTime(timeString: string): string {
  const [hours, minutes] = timeString.split(":").map(Number);
  const date = new Date();
  date.setHours(hours, minutes);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/**
 * Combine date and time strings into a Date object
 * @param dateString - ISO 8601 date (YYYY-MM-DD)
 * @param timeString - 24h format time (HH:MM)
 * @returns Date object
 */
export function parseMeetingDateTime(dateString: string, timeString: string): Date {
  return new Date(`${dateString}T${timeString}:00`);
}

/**
 * Check if a meeting is in the future
 * @param meeting - Meeting object
 * @returns True if meeting is upcoming
 */
export function isMeetingUpcoming(meeting: Meeting): boolean {
  const meetingDateTime = parseMeetingDateTime(meeting.meeting_date, meeting.meeting_time);
  return meetingDateTime > new Date() && meeting.status === "scheduled";
}

/**
 * Check if a meeting is in the past
 * @param meeting - Meeting object
 * @returns True if meeting date/time has passed
 */
export function isMeetingPast(meeting: Meeting): boolean {
  const meetingDateTime = parseMeetingDateTime(meeting.meeting_date, meeting.meeting_time);
  return meetingDateTime < new Date();
}

/**
 * Get badge color class based on meeting status
 * @param status - Meeting status
 * @returns Tailwind CSS color classes
 */
export function getMeetingStatusColor(status: MeetingStatus): string {
  const colorMap: Record<MeetingStatus, string> = {
    scheduled: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
    completed: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    cancelled: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
    rescheduled: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  };
  return colorMap[status] || colorMap.scheduled;
}

/**
 * Get icon for event type or meeting type
 * @param type - Event type (task, reminder, meeting) or meeting type (consultation, etc.)
 * @returns Icon emoji
 */
export function getMeetingTypeIcon(type: string): string {
  const iconMap: Record<string, string> = {
    // Event types
    task: "✅",
    reminder: "🔔",
    meeting: "📅",
    // Meeting types
    consultation: "👋",
    "follow-up": "🔄",
    "project-review": "🔍",
    "quote-discussion": "💬",
  };
  return iconMap[type] || "📅";
}

/**
 * Sort meetings by date and time (chronological order)
 * @param meetings - Array of meetings
 * @param ascending - Sort direction (default: true for upcoming first)
 * @returns Sorted meetings array
 */
export function sortMeetingsByDate(meetings: Meeting[], ascending = true): Meeting[] {
  return [...meetings].sort((a, b) => {
    const dateA = parseMeetingDateTime(a.meeting_date, a.meeting_time);
    const dateB = parseMeetingDateTime(b.meeting_date, b.meeting_time);
    return ascending ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
  });
}

/**
 * Get relative time description (e.g., "in 2 days", "yesterday")
 * @param dateString - ISO 8601 date
 * @returns Relative time string
 */
export function getRelativeTimeString(dateString: string): string {
  const date = new Date(dateString + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diffDays = Math.floor((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays === -1) return "Yesterday";
  if (diffDays > 1 && diffDays <= 7) return `In ${diffDays} days`;
  if (diffDays < -1 && diffDays >= -7) return `${Math.abs(diffDays)} days ago`;
  if (diffDays > 0) return formatMeetingDate(dateString);
  return formatMeetingDate(dateString);
}

/**
 * Format duration in minutes to human-readable string
 * @param minutes - Duration in minutes
 * @returns Formatted duration (e.g., "1h 30m")
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

/**
 * Validate if date is in the future (for scheduling new meetings)
 * @param dateString - ISO 8601 date
 * @param timeString - 24h format time
 * @returns True if date/time is in the future
 */
export function isFutureDateTime(dateString: string, timeString: string): boolean {
  const meetingDateTime = parseMeetingDateTime(dateString, timeString);
  return meetingDateTime > new Date();
}

/**
 * Get meetings grouped by upcoming vs past
 * @param meetings - Array of meetings
 * @returns Object with upcoming and past meetings arrays
 */
export function groupMeetingsByTime(meetings: Meeting[]): {
  upcoming: Meeting[];
  past: Meeting[];
} {
  const now = new Date();
  const upcoming: Meeting[] = [];
  const past: Meeting[] = [];

  meetings.forEach((meeting) => {
    const meetingDateTime = parseMeetingDateTime(meeting.meeting_date, meeting.meeting_time);
    if (meetingDateTime >= now && meeting.status === "scheduled") {
      upcoming.push(meeting);
    } else {
      past.push(meeting);
    }
  });

  return {
    upcoming: sortMeetingsByDate(upcoming, true), // Ascending (next meeting first)
    past: sortMeetingsByDate(past, false), // Descending (most recent first)
  };
}

/**
 * Format date to ISO 8601 string (YYYY-MM-DD)
 * Uses local timezone to avoid off-by-one errors
 * @param date - Date object
 * @returns ISO 8601 date string
 */
export function toISODateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Format time to HH:MM format
 * @param date - Date object
 * @returns 24h format time string
 */
export function toTimeString(date: Date): string {
  return date.toTimeString().slice(0, 5);
}
